import { isAllowEdit } from 'features/supplier/utils';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ReactComponent as DeleteIcon } from '@assets/icons/delete.svg';

import ImageUpload from '@components/ImageUpload';
import ModalContainer from '@components/modals/ModalContainer';
import NoData from '@components/NoData';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { fetchSupplierData, onAddGalleryImage, onRemovePhoto } from '@services/supplierService';

import { supplierCardRenderSequence } from '@utils/Util';

const chartToNum = {
	A: 1,
	B: 2,
	C: 3,
	D: 3
};

const Gallery = () => {
	const [supplierDocs, setSupplierDocs] = useState([]);
	const [uploadedImages, setUploadedImages] = useState([]);
	const [isAdd, setIsAdd] = useState(false);
	const { id } = useParams();
	const dispatch = useDispatch();
	const isLoading = useSelector((store) => store.loaderStore);

	useEffect(() => {
		onFetchGallery();
	}, []);

	const onFetchGallery = async () => {
		dispatch(openLoader());
		try {
			const responseData = await fetchSupplierData({
				type: 'GALLERY',
				supplierId: id
			});
			setSupplierDocs(responseData);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const onDeleteImage = async (docId) => {
		dispatch(openLoader());
		try {
			await onRemovePhoto({ id: docId, supplierId: id });
			onFetchGallery();
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const gallaryCardClassName = (idx, type) => {
		if (type === 'SINGLE') return 'gallery-item';
		else if (type === 'DOUBLE') return 'gallery-item one-half';
		else if (type === 'TRIPLE_C') {
			if (idx === 0) return 'gallery-item one-half';
			else return 'gallery-item one-third';
		} else if (type === 'TRIPLE_D') {
			if (idx === 2) return 'gallery-item one-half';
			else return 'gallery-item one-third';
		}
	};

	const renderGalleryItem = (doc, idx, type) => (
		<div className={`${gallaryCardClassName(idx, type)}`} key={doc.id}>
			<img src={doc?.docUrl} alt={doc?.name} />
			<h3 className="gallery-title text-sm md:text-xl font-bold text-white">{doc?.note}</h3>
			{isAllowEdit() && (
				<span className="gallery-action cursor-pointer" onClick={() => onDeleteImage(doc.id)}>
					<DeleteIcon />
				</span>
			)}
		</div>
	);

	const getImageCard = (galleryItems, cardItemCount) => {
		if (cardItemCount === 'A') {
			{
				return galleryItems?.map((doc, idx) => renderGalleryItem(doc, idx, 'SINGLE'));
			}
		} else if (cardItemCount === 'B') {
			{
				return galleryItems?.map((doc, idx) => renderGalleryItem(doc, idx, 'DOUBLE'));
			}
		} else if (cardItemCount === 'C') {
			{
				return galleryItems?.map((doc, idx) => renderGalleryItem(doc, idx, 'TRIPLE_C'));
			}
		} else if (cardItemCount === 'D') {
			{
				return galleryItems?.map((doc, idx) => renderGalleryItem(doc, idx, 'TRIPLE_D'));
			}
		}
	};

	const gallaryClassName = {
		A: 'single-item mb-2 sm:mb-4',
		B: 'two-in-row mb-2 sm:mb-4',
		C: 'three-in-row right-aligned mb-2 sm:mb-4',
		D: 'three-in-row left-aligned mb-2 sm:mb-4'
	};

	const onRenderGallery = () => {
		const cardArray = supplierCardRenderSequence(supplierDocs?.length);
		let startIndex = 0;
		return cardArray.map((cardItemCount, index) => {
			const galleryItems = supplierDocs.slice(startIndex, startIndex + chartToNum[cardItemCount]);
			startIndex += chartToNum[cardItemCount];
			return (
				<div className={`gallery-row ${gallaryClassName[cardItemCount]}`} key={index}>
					{getImageCard(galleryItems, cardItemCount)}
				</div>
			);
		});
	};

	const onUploadImage = (image) => {
		setUploadedImages([...uploadedImages, ...image]);
	};

	const onRemove = (id) => {
		let oldImages = uploadedImages;
		oldImages = oldImages?.filter((item) => id !== item.uniqueId);
		setUploadedImages(oldImages);
	};

	const onAddImages = async () => {
		let body = uploadedImages?.map((item) => {
			const { ...rest } = item;
			return { documentDTO: rest };
		});
		try {
			dispatch(openLoader());
			await onAddGalleryImage(id, body);
			await onFetchGallery();
			dispatch(closeLoader());
			setIsAdd(false);
		} catch {
			dispatch(closeLoader());
		}
	};

	return (
		<div className="Gallery-section hover-section section-padding">
			<h3 className="suplier-title pb-4 font-weight-bold regular-16">
				Gallery
				{isAllowEdit() && (
					<span className="ml-3">
						<button
							className="blue border-0 medium-14 bg-transparent"
							onClick={() => setIsAdd(true)}
						>
							Add
						</button>
					</span>
				)}
			</h3>
			{supplierDocs?.length === 0 ? (
				<NoData message="No photos added" />
			) : (
				<div className="factory-gallery">{onRenderGallery()}</div>
			)}
			{isAdd && (
				<ModalContainer>
					<div className="common-popup gallery-modal">
						<div className="common-popup-header d-flex justify-content-between">
							<div className="popup-tilte">
								<h3 className="semibold-16 mb-0">Gallery</h3>
							</div>
							<div className="close-btn" onClick={() => setIsAdd(false)}>
								<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
							</div>
						</div>
						<div className="common-popup-body">
							<div className="gallery-upload-and-items">
								<div className="single-item">
									<div className="upload-section">
										<ImageUpload
											size="sm"
											isUploadMulti={true}
											onUploadImage={onUploadImage}
											docType={'SUPPLIER_PHOTO_DOCUMENT'}
											type={'image'}
										/>
									</div>
								</div>

								{uploadedImages?.map((item, idx) => (
									<div className="single-item" key={idx}>
										<div className="image">
											<img src={item?.base64Str} alt="style" className="style-photo" />
											<div
												className="action-icon cursor-pointer"
												onClick={() => onRemove(item?.uniqueId)}
											>
												<img
													src={`${process.env.PUBLIC_URL}/icons/delete_gray.svg`}
													alt="check"
												/>
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
						<div className="modal-footer">
							<div className="footer-buttons text-right">
								<button
									className="button gray-outline size32 ml-2"
									onClick={() => setIsAdd(false)}
								>
									Cancel
								</button>
								<button
									disabled={isLoading || uploadedImages?.length === 0}
									className="button size32 ml-2"
									onClick={onAddImages}
								>
									Submit
								</button>
							</div>
						</div>
					</div>
				</ModalContainer>
			)}
		</div>
	);
};

export default Gallery;
